import cloneDeep from 'lodash/cloneDeep';
import React from 'react';
import ENDPOINT from '../../constants/endpoints';
import POPUP_STYLES from '../../constants/popup-styles';
import {POSITION_FLAG} from '../../constants/profile';
import {FirmActions} from '../../helpers/actions/firm';
import {DateHelpers} from '../../helpers/date-helpers';
import FileHelper from '../../helpers/file';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import ConfirmDialog from '../../scenes/Dialog/components/ConfirmDialog';
import FormDialog from '../../scenes/Dialog/components/FormDialog';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import EditCandidatePositionForm from '../../scenes/Form/components/EditCandidatePositionForm';
import ProfileAddReferenceForm from '../../scenes/Form/components/ProfileAddReferenceForm';
import ProfileEditAvatarForm from '../../scenes/Form/components/ProfileEditAvatarForm';
import ProfileEditCandidateInterviewDataRequestForm from '../../scenes/Form/components/ProfileEditCandidateInterviewDataRequestForm';
import ProfileEditConfidentialInfoForm from '../../scenes/Form/components/ProfileEditConfidentialInfoForm';
import ProfileEditEducationForm from '../../scenes/Form/components/ProfileEditEducationForm';
import ProfileEditPersonalInfoForm from '../../scenes/Form/components/ProfileEditPersonalInfoForm';
import ProfileEditPositionForm from '../../scenes/Form/components/ProfileEditPositionForm';
import ProfileEditPreferencesForm from '../../scenes/Form/components/ProfileEditPreferencesForm';
import ProfileEditSummaryForm from '../../scenes/Form/components/ProfileEditSummaryForm';
import ProfileRequestRecommendationForm from '../../scenes/Form/components/ProfileRequestRecommendationForm';
import UploadResumeForm from '../../scenes/Form/components/UploadResumeForm';

const action = {
  downloadResume: url => {
    FileHelper.downloadByUrlWithChecking(
      url,
      { act: 'downloadResume', page: 0 },
      {
        companyRight: 'You do not have permission to download resumes.',
        exist: 'File not found.',
      }
    );
  },

  editPhoto: (state, { setState, windowWidth, updateUserPicture }) => {
    FormDialog({
      compact: true,
      modeless: false,
      popupTitleStyles: {
        padding: parseInt(windowWidth, 10) < 480 ? '0 20px' : '0 25px',
      },
      popupInnerStyles: {
        padding: parseInt(windowWidth, 10) < 480 ? '0' : '0 20px',
        marginTop: '45px',
      },
      popupStyles: {
        minHeight: '30vh',
        maxWidth: '452px',
      },
      popupContentStyles: {
        paddingTop: 0,
      },
      form: (actions, params) => (
        <ProfileEditAvatarForm actions={actions} params={params} />
      ),
    }).then(
      ({ PictureUrl }) => {
        if (PictureUrl) {
          updateUserPicture(PictureUrl);
          setState({
            ...state,
            profile: {
              ...state.profile,
              PictureUrl: PictureUrl,
            },
          });
        }
      },
      response => ResponseErrorDialog({ response })
    );
  },

  deletePhoto: (state, api, { setState, updateUserPicture }) => {
    ConfirmDialog({
      bodyRender: 'Are you sure you want to delete your profile image?',
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
    }).then(() => {
      api.post(
        ENDPOINT.ACTION.CANDIDATE_PROFILE_PICTURE_DELETE,
        {},
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: () => {
            updateUserPicture(null);
            setState({
              ...state,
              profile: {
                ...state.profile,
                PictureUrl: null,
              },
            });
          },
        }
      );
    });
  },

  uploadResume: (state, { setState, updateUserResume, hasResume }) => {
    FormDialog({
      modeless: false,
      compact: false,
      popupStyles: {
        paddingBottom: '30px',
      },
      dismissWhenClose: true,
      form: (actions, params) => (
        <UploadResumeForm actions={actions} params={params} />
      ),
    }).then(
      ({ ResumeUrl }) => {
        BasicDialog({
          bodyRender: (
            <p className="dialog__message">Resume was successfully uploaded.</p>
          ),
        });

        if (!hasResume) {
          updateUserResume();
        }

        setState(() => ({
          ...state,
          profile: {
            ...state.profile,
            ResumeUrl,
            ResumeUpdateData: DateHelpers.getCurrentDate(),
          },
        }));
      },
      response => ResponseErrorDialog({ response })
    );
  },

  addPosition: (params) => {
    return FormDialog({
      title: 'Add Position',
      modeless: false,
      compact: true,
      params,
      form: (actions, params) => (
        <EditCandidatePositionForm
          actions={actions}
          params={params}
          type={POSITION_FLAG.POSITION}
        />
      ),
    });
  },

  editPosition: (params) => {
    return FormDialog({
      title: 'Edit Position',
      modeless: false,
      compact: true,
      params,
      form: (actions, params) => {
        if (params.isMyProfile) {
          return (
            <ProfileEditPositionForm actions={actions} params={params} />
          );
        }

        return (
          <EditCandidatePositionForm
            actions={actions}
            params={params}
            type={POSITION_FLAG.POSITION}
          />
        );
      },
    })
  },

  addEducation: (isRecruiter, { loadData }) => {
    FormDialog({
      title: 'Add Education',
      modeless: false,
      compact: true,
      params: { isRecruiter, isMyProfile: true },
      form: (actions, params) => (
        <EditCandidatePositionForm
          actions={actions}
          params={params}
          type={POSITION_FLAG.EDUCATION}
        />
      ),
    }).then(
      logout => {
        if (logout) {
          window.location = '/logout';
        } else {
          loadData();
        }
      },
      response => ResponseErrorDialog({ response })
    );
  },

  editEducation: (state, { PositionID }, { setState }) => {
    FormDialog({
      title: 'Edit Education',
      modeless: false,
      compact: true,
      params: { id: PositionID },
      form: (actions, params) => (
        <ProfileEditEducationForm actions={actions} params={params} />
      ),
    }).then(
      ({ Description, startDate, endDate }) => {
        const { Positions } = state.profile;
        const updatedPositions = Positions.map(position =>
          PositionID === position.PositionID
            ? {
                ...position,
                Description,
                StartMonth: startDate ? startDate.month : null,
                StartYear: startDate ? startDate.year : null,
                EndMonth: endDate ? endDate.month : null,
                EndYear: endDate ? endDate.year : null,
              }
            : position
        ).sort((a, b) => {
          if (Number(a.Present) === 1) {
            return -1;
          }

          if (Number(b.Present) === 1) {
            return 1;
          }

          return Number(b.EndYear) - Number(a.EndYear);
        });

        setState({
          ...state,
          profile: {
            ...state.profile,
            Positions: [...updatedPositions],
          },
        });
      },
      response => ResponseErrorDialog({ response })
    );
  },

  addReference: (state, { setState }) => {
    FormDialog({
      title: 'Add Reference',
      modeless: false,
      compact: true,
      form: (actions, params) => (
        <ProfileAddReferenceForm actions={actions} params={params} />
      ),
    }).then(
      values => {
        if (state.profile.References.length < 4) {
          const updatedReferences = cloneDeep(state.profile.References);
          updatedReferences.push({
            ReferrerCompany: values.CompanyID.label,
            ReferrerEmail: values.Email,
            ReferrerName: values.Name,
            ReferrerPhone: values.Phone,
            ReferrerTitle: values.TitleID.label,
          });

          setState({
            ...state,
            profile: {
              ...state.profile,
              References: [...updatedReferences],
            },
          });
        }
      },
      response => ResponseErrorDialog({ response })
    );
  },

  editConfidentialInfo: (state, type, { setState }) => {
    FormDialog({
      title: `Edit ${type} ${type === 'Contact' ? 'Information' : ''}`,
      modeless: false,
      compact: true,
      popupStyles: {
        minHeight: '30vh',
      },
      params: { type },
      form: (actions, params) => (
        <ProfileEditConfidentialInfoForm actions={actions} params={params} />
      ),
    }).then(
      values => {
        const Confidential = {
          EmailAddress2: values.EmailAddress2,
          GmatName: values.GmatID.label,
          GpaName: values.GpaID.label,
          MathSatName: values.MathSatID.label,
          PhoneNumber1: values.PhoneNumber1,
          PhoneNumber2: values.PhoneNumber2,
          ScoreName: values.ScoreID.label,
          UndergraduateMajor: values.UndergraduateMajor,
          VerbalSatName: values.VerbalSatID.label,
          WritingSatName: values.WritingSatID.label,
        };
        setState({
          ...state,
          profile: {
            ...state.profile,
            Confidential: { ...Confidential },
          },
        });
      },
      response => ResponseErrorDialog({ response })
    );
  },

  editPersonalInfo: (state, { setState }) => {
    FormDialog({
      title: 'Edit Personal Information',
      modeless: false,
      compact: true,
      form: (actions, params) => (
        <ProfileEditPersonalInfoForm actions={actions} params={params} />
      ),
    }).then(
      ({
        ManagePersonalAccount,
        ImmediateStartOpportunities,
        CitizenshipID,
        Languages,
      }) => {
        setState({
          ...state,
          profile: {
            ...state.profile,
            Citizenship: [...CitizenshipID.map(item => item.label)],
            ManagePersonalAccount,
            ImmediateStartOpportunities,
            Languages: Languages
              ? Languages.map(value => ({
                  LanguageName: value.language.label,
                  LevelName: value.level.label,
                }))
              : null,
          },
        });
      },
      response => ResponseErrorDialog({ response })
    );
  },

  editPreferences: (state, { setState }) => {
    FormDialog({
      title: 'Edit Preferences',
      modeless: false,
      compact: true,
      form: (actions, params) => (
        <ProfileEditPreferencesForm actions={actions} params={params} />
      ),
    }).then(
      values => {
        setState({
          ...state,
          profile: {
            ...state.profile,
            Preferences: {
              buyside: values.Buyside.map(item => item.label),
              location: values.Location.map(item => item.label),
              fundsize: values.Fundsize.map(item => item.label),
            },
          },
        });
      },
      response => ResponseErrorDialog({ response })
    );
  },

  addRecommendation: ({ PositionID, type }) => {
    FormDialog({
      title: 'Ask for Recommendation',
      modeless: false,
      compact: true,
      params: { id: PositionID },
      form: (actions, params) => (
        <ProfileRequestRecommendationForm actions={actions} params={params} type={type} />
      ),
    }).then(
      () =>
        BasicDialog({
          bodyRender:
            'Your recommendation request has been sent to the intended recipient.',
        }),
      response => ResponseErrorDialog({ response })
    );
  },

  editSummary: (state, { setState }) => {
    FormDialog({
      title: 'Edit Summary',
      modeless: false,
      popupStyles: POPUP_STYLES.formPopupStyles,
      popupInnerStyles: POPUP_STYLES.formInnerStyles,
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      compact: true,
      form: (actions, params) => (
        <ProfileEditSummaryForm actions={actions} params={params} />
      ),
    }).then(
      values =>
        setState({
          ...state,
          profile: {
            ...state.profile,
            Summary: values.Summary || '',
          },
        }),
      response => ResponseErrorDialog({ response })
    );
  },

  beforeSetFirmRating: (api, user) => {
    const action = new FirmActions(api, user);
    return action.checkRatingUpdate(user);
  },

  setFirmRating: (state, api, user, { id, mark }, { setState }) => {
    const action = new FirmActions(api, user);
    action.setRating({ id, mark }).then(data => {
      if (data) {
        const Positions = state.profile.Positions.map(position =>
          position.FirmID === id
            ? { ...position, ...data, CandidateRating: true }
            : position
        );

        setState({
          ...state,
          profile: {
            ...state.profile,
            Positions,
          },
        });
      }
    });
  },

  editInterviewData: (state, { setState }) => {
    FormDialog({
      title: '',
      modeless: false,
      compact: true,
      popupStyles: {
        ...POPUP_STYLES.formPopupStyles,
        maxWidth: '540px',
      },
      popupInnerStyles: { ...POPUP_STYLES.formInnerStyles, marginTop: '30px' },
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      params: { initialValues: { ...state.profile.InterviewData } },
      form: (actions, params) => (
        <ProfileEditCandidateInterviewDataRequestForm
          actions={actions}
          params={params}
        />
      ),
    }).then(
      data => {
        setState({
          ...state,
          profile: {
            ...state.profile,
            InterviewData: { ...data },
          },
        });
      },
      response => ResponseErrorDialog({ response })
    );
  },
};

export default action;
